import { blue, green, purple, shade, yellow } from '../../../colors'
import * as yup from 'yup'
import { type KeyStatKey, type DisplaySettings, type ReportChartType } from '../types'

export enum CellField {
  INSTRUCTIONDETAILS = 'instructorDetails',
}

export enum FlaggedCommentType {
  FLAGGED = 'FLAGGED',
  UNFLAGGED = 'UNFLAGGED',
  STARRED = 'STARRED',
}

export enum KeyStatType {
  AVERAGE = 'Average',
  TOP_2 = 'Top 2',
  BOTTOM_2 = 'Bottom 2',
  STANDARD_DEVIATION = 'Standard Deviation',
  MEDIAN = 'Median',
  MODE = 'Mode',
  DIST = 'Dist'
}

export const keyStatSwitches: Array<{ label: string, name: KeyStatKey, calculationName: KeyStatType }> = [
  { label: 'Average', name: 'average', calculationName: KeyStatType.AVERAGE },
  { label: 'Median', name: 'median', calculationName: KeyStatType.MEDIAN },
  { label: 'Mode', name: 'mode', calculationName: KeyStatType.MODE },
  { label: 'Top 2', name: 'top2', calculationName: KeyStatType.TOP_2 },
  { label: 'Bottom 2', name: 'bottom2', calculationName: KeyStatType.BOTTOM_2 },
  { label: 'Standard Deviation', name: 'stdev', calculationName: KeyStatType.STANDARD_DEVIATION }
]

export const allCalculationKeys = keyStatSwitches.map(({ name }) => name)

export const defaultVisibleKeyStatNames: KeyStatKey[] = ['average', 'top2']

export const chartTypeDefaultVisibleKeyStatNames: Record<ReportChartType, KeyStatKey[]> = {
  distribution: ['average', 'median', 'mode', 'stdev'],
  average: ['stdev'],
  top2: [],
  bottom2: []
}

export const reportChartTypeOptions: Array<{ label: string, value: ReportChartType }> = [
  { label: 'Distribution', value: 'distribution' },
  { label: 'Average', value: 'average' },
  { label: 'Top 2', value: 'top2' },
  { label: 'Bottom 2', value: 'bottom2' }
]

export const statButtons = [
  { label: 'AVG', value: KeyStatType.AVERAGE },
  { label: 'TOP 2', value: KeyStatType.TOP_2 },
  { label: 'BOT 2', value: KeyStatType.BOTTOM_2 },
  { label: 'MODE', value: KeyStatType.MODE }
]

export const chartSettingsSwitches: Array<{ label: string, name: keyof DisplaySettings['chartSettings'] }> = [
  { label: 'Display Percentage', name: 'percentage' }
]

export const keyStatKeyToCalculationName: Record<KeyStatKey, KeyStatType> = {
  average: KeyStatType.AVERAGE,
  median: KeyStatType.MEDIAN,
  mode: KeyStatType.MODE,
  top2: KeyStatType.TOP_2,
  bottom2: KeyStatType.BOTTOM_2,
  stdev: KeyStatType.STANDARD_DEVIATION
}

export const reportChartTypeToCalculationName: Record<Exclude<ReportChartType, 'distribution'>, KeyStatType> = {
  average: KeyStatType.AVERAGE,
  top2: KeyStatType.TOP_2,
  bottom2: KeyStatType.BOTTOM_2
}

export const barColors = [blue[600], shade[850], green[250], green[700], yellow[150], yellow[450], purple[300], purple[700]]
export const barColorsForLastTerms = [shade[850], blue[600], green[250]]
export const responseFilterBaselineColor = shade[850]
export const responseFilterBaselineTextColor = blue[700]
export const responseFilterActiveColor = blue[600]

export const comparisonSchema = yup.object({
  comparisons: yup.array().of(
    yup.object({
      compareBy: yup.string().nullable(),
      comparison: yup.string().nullable().when('compareBy', {
        is: (val: string | null) => val?.trim().toLowerCase() === 'academic unit',
        then: schema => schema.required('Select an Academic Unit to continue'),
        otherwise: schema => schema.nullable()
      }),
      // Keep comparison display metadata in the validated payload so the
      // pending badge state can render the selected comparison labels
      // immediately while the refreshed report is still loading.
      comparisonOption: yup.object({
        id: yup.string().required(),
        title: yup.string().required(),
        abbreviation: yup.string().nullable()
      }).nullable()
    })
  ).required(),
  slices: yup.array().of(
    yup.object({
      sliceBy: yup.string().nullable(),
      attributeValueId: yup.string().nullable(),
      attributeValue: yup.string().nullable(),
      attributeKey: yup.string().nullable(),
      isSelected: yup.boolean().nullable()
    })
  ).required(),
  responseFilter: yup.object({
    itemId: yup.string().nullable(),
    revision: yup.number().nullable(),
    prompt: yup.string().nullable(),
    answers: yup.array().of(yup.string().required()).when('itemId', {
      is: (val: string | null) => Boolean(val),
      then: schema => schema.min(1, 'Select at least one answer to continue').required(),
      otherwise: schema => schema.default([])
    }),
    sectionTitle: yup.string().nullable(),
    questionNumberLabel: yup.string().nullable()
  }).nullable(),
  chartSettings: yup.object(
    chartSettingsSwitches.reduce<Record<string, yup.BooleanSchema>>((acc, { name }) => {
      acc[name] = yup.boolean()
      return acc
    }, {})
  ).required()
})
